import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { motion } from 'motion/react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import type { StorylineChartEvent } from '@/mocks/marketStorylinesMock';
import { stockService } from '@/services/api';
import {
  buildWholeDollarYAxisTicks,
  formatPriceAxisTick,
} from '@/utils/historicalChartWholeDollarTicks';
import { getStorylineEventColor } from '@/utils/storylineEventColors';

type Props = {
  chartSymbol: string;
  chartLabel?: string;
  /** Override ticker used when fetching prices (e.g. SIXV → XLV). */
  priceSymbol?: string;
  events: StorylineChartEvent[];
  days?: number;
  className?: string;
  compact?: boolean;
  large?: boolean;
  showEventChrome?: boolean;
  /** Prefix for shared selection keys across a chart grid (usually chartSymbol). */
  eventKeyPrefix?: string;
  /** Assign a unique color per catalyst event instead of sentiment colors. */
  distinctEventColors?: boolean;
  selectedEventKey?: string | null;
  onSelectedEventKeyChange?: (key: string | null) => void;
};

export function buildStorylineEventKey(
  symbol: string,
  event: Pick<StorylineChartEvent, 'date' | 'label'>,
): string {
  return `${symbol}::${event.date}::${event.label}`;
}

type ChartRow = {
  date: string;
  price: number;
};

const LINE_COLOR = '#374151';

function formatXTick(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { month: 'short' });
  } catch {
    return dateStr;
  }
}

function formatEventDate(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function buildXTicks(dates: string[]): string[] {
  if (dates.length <= 6) return dates;
  const ticks: string[] = [];
  const seenMonths = new Set<string>();
  for (const date of dates) {
    const monthKey = date.slice(0, 7);
    if (!seenMonths.has(monthKey)) {
      seenMonths.add(monthKey);
      ticks.push(date);
    }
  }
  const last = dates[dates.length - 1];
  if (ticks[ticks.length - 1] !== last) ticks.push(last);
  if (ticks.length < 4) {
    const step = Math.max(1, Math.floor((dates.length - 1) / 5));
    const spaced: string[] = [];
    for (let i = 0; i < dates.length; i += step) spaced.push(dates[i]);
    if (spaced[spaced.length - 1] !== last) spaced.push(last);
    return spaced;
  }
  return ticks;
}

function eventDotColor(
  event: StorylineChartEvent,
  distinctEventColors: boolean,
): string {
  return getStorylineEventColor(event, { distinct: distinctEventColors });
}

/** Snap catalyst date to nearest trading day in chart data (weekends/holidays). */
export function resolveEventAnchorDate(chartDates: string[], eventDate: string): string | null {
  if (!chartDates.length) return null;
  if (chartDates.includes(eventDate)) return eventDate;
  const target = new Date(`${eventDate}T12:00:00`).getTime();
  let best: string | null = null;
  let bestDiff = Infinity;
  for (const date of chartDates) {
    const diff = Math.abs(new Date(`${date}T12:00:00`).getTime() - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = date;
    }
  }
  // Only snap within ~5 calendar days so pre-IPO names do not get spurious dots.
  if (best && bestDiff <= 5 * 86_400_000) return best;
  return null;
}

type AnchoredEvent = {
  event: StorylineChartEvent;
  anchorDate: string;
};

function ChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartRow }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="mb-0.5 text-gray-500">{formatEventDate(row.date)}</p>
      <p className="font-medium tabular-nums text-gray-900">${row.price.toFixed(2)}</p>
    </div>
  );
}

function EventDot({
  cx,
  cy,
  event,
  isSelected,
  onSelect,
  distinctEventColors,
}: {
  cx?: number;
  cy?: number;
  event: StorylineChartEvent;
  isSelected: boolean;
  onSelect: () => void;
  distinctEventColors: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);

  if (cx == null || cy == null) return null;

  const fill = eventDotColor(event, distinctEventColors);
  const baseRadius = 5;
  const hoverRadius = 8;
  const dotRadius = isSelected ? baseRadius : isHovered ? hoverRadius : baseRadius;

  const handleClick = (e: ReactMouseEvent) => {
    e.stopPropagation();
    onSelect();
  };

  return (
    <g
      data-chart-interactive-marker="true"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={16}
        fill="transparent"
        strokeWidth={0}
        pointerEvents="all"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
      />
      {isSelected ? (
        <motion.circle
          cx={cx}
          cy={cy}
          r={dotRadius}
          fill="none"
          stroke={fill}
          strokeWidth={1.5}
          strokeOpacity={0}
          animate={{
            r: [dotRadius, 16],
            strokeOpacity: [0.4, 0],
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeOut',
            repeatDelay: 0.2,
          }}
          style={{ pointerEvents: 'none' }}
        />
      ) : null}
      <circle
        cx={cx}
        cy={cy}
        r={dotRadius}
        fill={fill}
        strokeWidth={0}
        pointerEvents="none"
        style={{ transition: 'r 0.2s ease-in-out' }}
      />
    </g>
  );
}

function EventExplanation({
  event,
  price,
  onClose,
}: {
  event: StorylineChartEvent;
  price: number | null;
  onClose: () => void;
}) {
  const accentBorder =
    event.sentiment === 'positive'
      ? 'border-emerald-100'
      : event.sentiment === 'negative'
        ? 'border-rose-100'
        : 'border-indigo-100';
  const accentBg =
    event.sentiment === 'positive'
      ? 'bg-emerald-50/60'
      : event.sentiment === 'negative'
        ? 'bg-rose-50/60'
        : 'bg-indigo-50/60';

  return (
    <div className={`mt-4 rounded-xl border ${accentBorder} ${accentBg} p-4`}>
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{event.label}</p>
          <p className="text-xs text-gray-500">
            {formatEventDate(event.date)}
            {price != null ? ` · $${price.toFixed(2)}` : ''}
            {event.percentChange != null
              ? ` · ${event.percentChange > 0 ? '+' : ''}${event.percentChange.toFixed(2)}%`
              : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-xs text-gray-500 hover:text-gray-800"
        >
          Close
        </button>
      </div>
      {event.primaryCatalyst ? (
        <p className="mb-2 text-sm font-medium text-gray-800">{event.primaryCatalyst}</p>
      ) : null}
      <p className="text-sm leading-relaxed text-gray-700">{event.summary}</p>
    </div>
  );
}

export function StorylineSectorChart({
  chartSymbol,
  chartLabel,
  priceSymbol,
  events,
  days = 150,
  className = '',
  compact = false,
  large = false,
  showEventChrome = true,
  eventKeyPrefix,
  distinctEventColors = false,
  selectedEventKey: controlledSelectedEventKey,
  onSelectedEventKeyChange,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [priceRows, setPriceRows] = useState<ChartRow[]>([]);
  const [internalSelectedEventKey, setInternalSelectedEventKey] = useState<string | null>(null);
  const fetchSymbol = priceSymbol ?? chartSymbol;
  const selectionPrefix = eventKeyPrefix ?? chartSymbol;
  const selectedEventKey = controlledSelectedEventKey ?? internalSelectedEventKey;
  const chartHeight = large ? 500 : compact ? 180 : 260;
  const loadingHeight = large ? 520 : compact ? 220 : 280;
  const effectiveDays = days;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await stockService.getHistoricalPrices(fetchSymbol, effectiveDays);
        if (cancelled) return;
        if (!data?.length) {
          setError(`No price data available for ${chartSymbol}`);
          setPriceRows([]);
          return;
        }
        setPriceRows(
          data.map((row) => ({
            date: row.date,
            price: row.close,
          })),
        );
      } catch {
        if (!cancelled) {
          setError(`Failed to load ${chartSymbol} chart data`);
          setPriceRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [fetchSymbol, effectiveDays]);

  const chartData = priceRows;

  const xTicks = useMemo(() => buildXTicks(chartData.map((r) => r.date)), [chartData]);

  const { yAxisDomain, yAxisTicks, yAxisTickStep } = useMemo(() => {
    const prices = chartData.map((r) => r.price).filter(Number.isFinite);
    if (!prices.length) {
      return {
        yAxisDomain: [0, 100] as [number, number],
        yAxisTicks: [0, 50, 100],
        yAxisTickStep: 50,
      };
    }
    const dataMin = Math.min(...prices);
    const dataMax = Math.max(...prices);
    const span = dataMax - dataMin;
    const pad = span > 0 ? span * 0.08 : dataMax * 0.05;
    const domainMin = Math.max(0, dataMin - pad);
    const domainMax = Math.max(dataMax + pad, domainMin + 0.01);
    const built = buildWholeDollarYAxisTicks(domainMin, domainMax);
    return {
      yAxisDomain: [domainMin, domainMax] as [number, number],
      yAxisTicks: built.ticks,
      yAxisTickStep: built.step,
    };
  }, [chartData]);

  const anchoredEvents = useMemo((): AnchoredEvent[] => {
    const chartDates = chartData.map((row) => row.date);
    const out: AnchoredEvent[] = [];
    for (const event of events) {
      const anchorDate = resolveEventAnchorDate(chartDates, event.date);
      if (anchorDate) out.push({ event, anchorDate });
    }
    return out;
  }, [events, chartData]);

  const visibleEvents = useMemo(
    () => anchoredEvents.map((item) => item.event),
    [anchoredEvents],
  );

  const selectedEvent = useMemo(() => {
    if (selectedEventKey) {
      return (
        visibleEvents.find(
          (event) => buildStorylineEventKey(selectionPrefix, event) === selectedEventKey,
        ) ?? null
      );
    }
    return null;
  }, [visibleEvents, selectedEventKey, selectionPrefix]);

  const selectedPrice = useMemo(() => {
    if (!selectedEvent) return null;
    const anchor = anchoredEvents.find((item) => item.event === selectedEvent)?.anchorDate;
    const date = anchor ?? selectedEvent.date;
    return chartData.find((row) => row.date === date)?.price ?? null;
  }, [anchoredEvents, chartData, selectedEvent]);

  const handleSelectEvent = (event: StorylineChartEvent) => {
    const key = buildStorylineEventKey(selectionPrefix, event);
    const next = selectedEventKey === key ? null : key;
    if (onSelectedEventKeyChange) {
      onSelectedEventKeyChange(next);
    } else {
      setInternalSelectedEventKey(next);
    }
  };

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center rounded-2xl border border-gray-200 bg-white ${className}`}
        style={{ height: loadingHeight }}
      >
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-gray-400" />
        <span className="text-sm text-gray-500">Loading {chartSymbol} chart...</span>
      </div>
    );
  }

  if (error || !chartData.length) {
    return (
      <div
        className={`flex h-[200px] items-center justify-center rounded-2xl border border-gray-200 bg-white p-6 text-center ${className}`}
      >
        <p className="text-sm text-gray-500">{error ?? 'Chart unavailable'}</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className={`flex items-baseline justify-between gap-3 ${large ? 'mb-4' : compact ? 'mb-2' : 'mb-3'}`}>
        <div>
          <p className={`font-medium text-gray-900 ${large ? 'text-base' : compact ? 'text-xs' : 'text-sm'}`}>
            {chartLabel ?? chartSymbol}
          </p>
          {!compact ? (
            <p className="text-xs text-gray-500">
              {formatEventDate(chartData[0].date)} – {formatEventDate(chartData[chartData.length - 1].date)}
            </p>
          ) : null}
        </div>
        {showEventChrome ? (
          <p className="text-xs text-gray-400">Click a dot for event context</p>
        ) : null}
      </div>

      <div className={`rounded-2xl border border-gray-200 bg-white ${large ? 'p-5' : compact ? 'p-3' : 'p-4'}`}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart
            data={chartData}
            margin={{
              top: large ? 12 : 12,
              right: large ? 16 : 16,
              left: large ? 8 : 4,
              bottom: large ? 28 : 4,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f3f4f6"
              vertical={false}
              syncWithTicks
            />
            <XAxis
              dataKey="date"
              ticks={xTicks}
              tickFormatter={formatXTick}
              tick={{ fill: '#9ca3af', fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              dy={8}
            />
            <YAxis
              domain={yAxisDomain}
              ticks={yAxisTicks}
              tick={{ fill: '#9ca3af', fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => formatPriceAxisTick(v, yAxisTickStep)}
              dx={large ? -8 : 0}
              width={large ? 56 : 48}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: '#d1d5db', strokeWidth: 1 }} />
            <Line
              type="monotone"
              dataKey="price"
              stroke={LINE_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={false}
            />
            {anchoredEvents.map(({ event, anchorDate }) => {
              const row = chartData.find((r) => r.date === anchorDate);
              if (!row) return null;
              const eventKey = buildStorylineEventKey(selectionPrefix, event);
              const sameDayCount = anchoredEvents.filter((e) => e.anchorDate === anchorDate).length;
              const sameDayIndex = anchoredEvents
                .filter((e) => e.anchorDate === anchorDate)
                .findIndex((e) => e.event.label === event.label);
              const yOffset =
                sameDayCount > 1 ? (sameDayIndex - (sameDayCount - 1) / 2) * 14 : 0;
              return (
                <ReferenceDot
                  key={eventKey}
                  x={anchorDate}
                  y={row.price}
                  shape={(props) => (
                    <EventDot
                      cx={props.cx}
                      cy={props.cy != null ? props.cy + yOffset : props.cy}
                      event={event}
                      isSelected={selectedEventKey === eventKey}
                      distinctEventColors={distinctEventColors}
                      onSelect={() => handleSelectEvent(event)}
                    />
                  )}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>

        {showEventChrome && visibleEvents.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
            {visibleEvents.map((event) => {
              const eventKey = buildStorylineEventKey(selectionPrefix, event);
              return (
              <button
                key={`${eventKey}-legend`}
                type="button"
                onClick={() => handleSelectEvent(event)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs transition-colors ${
                  selectedEventKey === eventKey
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: eventDotColor(event, distinctEventColors) }}
                />
                {event.label}
              </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {showEventChrome && selectedEvent ? (
        <EventExplanation
          event={selectedEvent}
          price={selectedPrice}
          onClose={() => {
            if (onSelectedEventKeyChange) {
              onSelectedEventKeyChange(null);
            } else {
              setInternalSelectedEventKey(null);
            }
          }}
        />
      ) : null}
    </div>
  );
}
